import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button, Form } from 'react-bootstrap'
import { FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast'
import moment from 'moment';
import { deleteSubCategory, getSubCategoryListingPaginate, statusSubCatUpdate, vtoStatusSubCatUpdate } from '../../api/context/subcategory';
import CustomisedModal from '../../components/Modal/CustomisedModal';
import { IoMdArrowBack } from "react-icons/io";
import { IoSearchOutline } from 'react-icons/io5';
import { checkUserPermissions } from '../../helpers/permission';
import Loader from '../../components/loader/loader';
import { Box, Paper, Switch, Tooltip } from '@mui/material';
import DataTable from 'react-data-table-component';

const queueDatatableCustomStyles = {
    headCells: {
        style: {
            display: "flex",
            justifyContent: "center",
            fontWeight: '600',
            fontSize: '14px',
            padding: '16px 8px',
            backgroundColor: '#f9f9f9ff',
        },
    },
    cells: {
        style: {
            display: "flex",
            fontSize: '12px',
            fontWeight: '500',
            padding: '12px',
        },
    },
};

const SubCategories = () => {
    const { state } = useLocation();
    const subcat = state?.data?.title;
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [update, setUpdate] = useState(false);
    const [subcategory, setSubCategory] = useState([]);
    const [modal, setModal] = useState(false);
    const [deletingId, setDeletingId] = useState("");

    const [search, setSearch] = useState('');

    const PAGE_LIMIT = 1000;

    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const isVendor = user?.userType === 'Vendor';
    const canManage = checkUserPermissions('manage_sub_category');

    const toggleModal = () => setModal(prev => !prev);

    const handleConfirm = (rowId) => {
        setDeletingId(rowId);
        setModal(true);
    };

    const statusActive = (rowId) => {
        const previous = [...subcategory];
        setSubCategory(prev =>
            prev.map(item =>
                item.id === rowId ? { ...item, isActive: !item.isActive } : item
            )
        );
        statusSubCatUpdate(rowId)((response) => {
            if (response?.data?.statusCode !== 200) {
                setSubCategory(previous);
                toast.error("Failed to update status");
            }
        });
    };

    const vtoStatusActive = (rowId) => {
        const previous = [...subcategory];
        setSubCategory(prev =>
            prev.map(item =>
                item.id === rowId ? { ...item, isAllowedForVTO: !item.isAllowedForVTO } : item
            )
        );
        vtoStatusSubCatUpdate(rowId)((response) => {
            if (response?.data?.statusCode !== 200) {
                setSubCategory(previous);
                toast.error("Failed to update VTO status");
            }
        });
    };

    const deleteSubCategories = (rowId) => {
        deleteSubCategory(rowId)((response) => {
            if (response?.data?.statusCode == 200) {
                toast.success("Sub Category removed");
                setModal(false);
                setUpdate(u => !u);
            } else {
                toast.error(response?.data?.message || "Failed to remove");
            }
        });
    };

    const getAllSubCategories = () => {
        setLoading(true);
        getSubCategoryListingPaginate({ id, page: 1, limit: PAGE_LIMIT })((response) => {
            if (response?.statusCode == 200) {
                setSubCategory(response?.data?.subSubCategory || []);
            } else {
                setSubCategory([]);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        getAllSubCategories();
    }, [update]);

    const filteredSubCategories = subcategory.filter((item) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (item?.title || '').toLowerCase().includes(q);
    });

    const columns = [
        {
            name: "S.No.",
            selector: (_row, index) => index + 1,
            grow: 1,
            center: true,
        },
        {
            name: 'Image',
            cell: (row) => (
                <div
                    style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        overflow: 'hidden',
                        background: '#f4f4f4',
                        border: '1px solid #ececec',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {row?.img ? (
                        <img
                            src={row.img}
                            alt={row?.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <span style={{ fontSize: 11, color: '#a5a5a5' }}>No img</span>
                    )}
                </div>
            ),
            grow: 1,
            center: true,
        },
        {
            name: 'Title',
            selector: (row) => row?.title,
            cell: (row) => (
                <Tooltip title={row?.title || ''} disableInteractive placement="top">
                    <div
                        style={{
                            width: '100%',
                            cursor: canManage ? 'pointer' : 'default',
                            fontWeight: 600,
                            color: '#1f1f1f',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'center',
                        }}
                        onClick={() => canManage && navigate(`/create-sub-categories/${row.id}`, {
                            state: { subCategoryData: row }
                        })}
                    >
                        {row?.title}
                    </div>
                </Tooltip>
            ),
            sortable: false,
            grow: 1,
            center: true,
        },
        {
            name: 'Created On',
            selector: (row) => row?.createdAt,
            cell: (row) => (
                <div style={{ color: '#555' }}>
                    {row?.createdAt ? moment(row.createdAt).format('DD MMM YYYY') : '-'}
                </div>
            ),
            grow: 1,
            center: true,
        },
        ...(canManage ? [{
            name: 'Status',
            cell: (row) => (
                <div className="d-flex justify-content-center" onClick={(e) => e.stopPropagation()}>
                    <Switch
                        size="small"
                        checked={!!row?.isActive}
                        onChange={() => statusActive(row?.id)}
                        className="custom-switch"
                    />
                </div>
            ),
            grow: 1,
            center: true,
            ignoreRowClick: true,
        }] : []),
        ...(canManage ? [{
            name: 'VTO Status',
            cell: (row) => (
                <div className="d-flex justify-content-center" onClick={(e) => e.stopPropagation()}>
                    <Switch
                        size="small"
                        checked={!!row?.isAllowedForVTO}
                        onChange={() => vtoStatusActive(row?.id)}
                        className="custom-switch"
                    />
                </div>
            ),
            grow: 1,
            center: true,
            ignoreRowClick: true,
        }] : []),
        ...(canManage ? [{
            name: 'Action',
            cell: (row) => (
                <div className="d-flex justify-content-center" onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex flex-row gap-3" role="button">
                        <Tooltip title="Edit" disableInteractive placement="top">
                            <div
                                className="edit_icon_bg"
                                onClick={() => navigate(`/create-sub-categories/${row.id}`, {
                                    state: { subCategoryData: row }
                                })}
                            >
                                <FiEdit className="edit_icon text-white fs-5" />
                            </div>
                        </Tooltip>
                        <Tooltip title="Delete" disableInteractive placement="top">
                            <div
                                className="delete_icon_bg"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfirm(row?.id);
                                }}
                            >
                                <FiTrash2 className="delete_icon text-black fs-5" />
                            </div>
                        </Tooltip>
                    </div>
                </div>
            ),
            grow: 1,
            center: true,
            ignoreRowClick: true,
        }] : []),
    ];

    return (
        <>
            <div>
                <div className="page-header-sticky">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <button
                                type="button"
                                onClick={() => navigate('/categories')}
                                className="border-0 bg-transparent p-0"
                                style={{ lineHeight: 0, cursor: 'pointer' }}
                                aria-label="Back to categories"
                            >
                                <IoMdArrowBack className="fs-3 text-black" />
                            </button>
                            <h3 className="mb-0 fw-bold">
                                {isVendor
                                    ? (subcat ? `${subcat} Collection` : 'Sub Categories')
                                    : (subcat ? `${subcat} Sub Categories` : 'Sub Categories')}
                            </h3>
                        </div>

                        {canManage && !isVendor && (
                            <Button
                                className="request_btn"
                                onClick={() => navigate(`/create-sub-categories`, {
                                    state: {
                                        categoryId: id,
                                        categoryName: subcat,
                                    }
                                })}
                            >
                                <FiPlus size={18} strokeWidth={3} />
                                Add New SubCategory
                            </Button>
                        )}
                    </div>

                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div style={{ flex: 1, minWidth: '260px' }}>
                            <Form.Control
                                type="text"
                                className="form-control"
                                placeholder="Search sub categories..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{
                                    minHeight: '40px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                }}
                            />
                        </div>
                    </div>
                </div>

                {isVendor ? (
                            loading ? (
                                <div style={{ minHeight: '60vh' }}><Loader /></div>
                            ) : (
                                <div className="subcategories-vendor-grid">
                                    {filteredSubCategories.length > 0 ? (
                                        filteredSubCategories.map((row) => (
                                            <div className="subcat-card" key={row?.id}>
                                                {row?.img ? (
                                                    <img src={row.img} alt={row?.title} />
                                                ) : (
                                                    <div className="no-image">No image</div>
                                                )}
                                                <div className="title-overlay">
                                                    <h3>{row?.title}</h3>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="subcat-empty">No Sub Categories Found</div>
                                    )}
                                </div>
                            )
                        ) : (
                            <Box className="products-and-options" sx={{ mt: 1 }}>
                                <Paper square className="shadow-none border border-blue-grey-200">
                                    <DataTable
                                        columns={columns}
                                        data={filteredSubCategories}
                                        progressPending={loading}
                                        progressComponent={<div style={{ height: '60vh' }}><Loader /></div>}
                                        highlightOnHover
                                        pointerOnHover={canManage}
                                        customStyles={queueDatatableCustomStyles}
                                        noDataComponent={
                                            <div style={{ padding: '50px 0', textAlign: 'center', color: '#7a7a7a', fontSize: 14 }}>
                                                No Sub Categories Found
                                            </div>
                                        }
                                    />
                                </Paper>
                            </Box>
                        )}

                <CustomisedModal
                    isOpen={modal}
                    customClass={"policy_modal"}
                    toggle={toggleModal}
                >
                    <h5>Are you sure you want to remove this item?</h5>
                    <div className="policy_Agree">
                        <Button
                            onClick={() => setModal(false)}
                            size="lg"
                            className="btn_cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="lg"
                            className="btn"
                            onClick={() => deleteSubCategories(deletingId)}
                        >
                            Remove
                        </Button>
                    </div>
                </CustomisedModal>
            </div>
        </>
    );
};

export default SubCategories;
